import { useEffect, useState } from 'react';
import { ImageOff } from 'lucide-react';
import { isVideoContent } from '@/features/campaigns/utils/resolveCreativePreview';

type ContentPreviewMediaProps = {
  url: string;
  type?: string;
  alt?: string;
  className?: string;
  /** Thumbnail mode (muted, no controls). */
  thumb?: boolean;
};

function setVideoNoReferrer(el: HTMLVideoElement | null) {
  el?.setAttribute('referrerpolicy', 'no-referrer');
}

/**
 * Renders image or video for campaign content. If the first guess fails
 * (e.g. video URL in an <img>, or private URL), tries the other tag once.
 */
export function ContentPreviewMedia({
  url,
  type = '',
  alt = 'Content preview',
  className = 'h-full w-full object-cover',
  thumb = false,
}: ContentPreviewMediaProps) {
  const preferVideo = isVideoContent(type, url);
  const [mode, setMode] = useState<'video' | 'image' | 'failed'>(
    preferVideo ? 'video' : 'image',
  );
  const [triedAlt, setTriedAlt] = useState(false);

  useEffect(() => {
    setMode(isVideoContent(type, url) ? 'video' : 'image');
    setTriedAlt(false);
  }, [url, type]);

  if (!url || mode === 'failed') {
    return (
      <div
        className={`flex min-h-[10rem] items-center justify-center bg-[var(--dash-ink)] text-white/45 ${className}`}
      >
        <div className="flex flex-col items-center gap-1 px-2 text-center">
          <ImageOff className="h-4 w-4" />
          {!thumb ? (
            <span className="text-[10px] font-medium">Preview unavailable</span>
          ) : null}
        </div>
      </div>
    );
  }

  const onError = () => {
    if (!triedAlt) {
      setTriedAlt(true);
      setMode((m) => (m === 'video' ? 'image' : 'video'));
      return;
    }
    setMode('failed');
  };

  if (mode === 'video') {
    return (
      <video
        key={`v-${url}`}
        src={url}
        className={className}
        muted={thumb}
        playsInline
        controls={!thumb}
        preload="metadata"
        ref={setVideoNoReferrer}
        onError={onError}
      />
    );
  }

  return (
    <img
      key={`i-${url}`}
      src={url}
      alt={alt}
      className={className}
      referrerPolicy="no-referrer"
      onError={onError}
    />
  );
}
